import { useMemo, useState, useEffect } from 'react';
import { getLocale } from 'umi';
import { useMemoizedFn } from 'ahooks';
import useSearchGenres from './useSearchGenres';
import { getTextWithHighlight } from '@intelligence/utils/commonUtils';
import { analysisGenreStr } from '../utils';
import { LANGUAGE_CN } from '@intelligence/_config/config';

import type { ISelectOptionWithGroup, IGenreMetadata } from '@intelligence/types/common';


export default function useFeedbackGenreSelect(props: {
  genres: IGenreMetadata[];
  genre: string;
  resetFlag: boolean;
  ignoreCase?: boolean;
  separate?: boolean;
}) {
  const { genres, genre, resetFlag, ignoreCase, separate } = props || {};

  const locale = getLocale();

  const { keywords, onSearch, onReset } = useSearchGenres();

  const [value, setValue] = useState<string[]>(analysisGenreStr(genre));

  const options = useMemo<ISelectOptionWithGroup[]>(() => {
    if (!Array.isArray(genres)) return [];
    const result = [];
    for (const genre of genres) {
      const genreCode = genre?.genre;
      if (!genreCode) continue;
      const genreEN = genre?.genre_en;
      const genreCN = genre?.genre_cn;
      let text = locale === LANGUAGE_CN ? genreCN : genreEN;
      text = text || genreEN || genreCN || genre?.genre;
      result.push({
        text: getTextWithHighlight(text, keywords, { isIgnoreCase: ignoreCase, isSeparate: separate }),
        label: text,
        value: genreCode,
      });
    }
    return result;
  }, [locale, genres, ignoreCase, separate, keywords]);

  const handleDelete = useMemoizedFn((payload: string) => {
    if (!Array.isArray(value)) return;
    const valueTmp = value.filter((item) => item !== payload);
    setValue(valueTmp);
  });

  const handleChange = useMemoizedFn((payload: string[]) => {
    if (!Array.isArray(payload)) return;
    setValue(payload);
  });

  const handleOpen = useMemoizedFn(() => {
    onReset();
  });

  useEffect(() => {
    if (!resetFlag) return;
    setValue(analysisGenreStr(genre));
  }, [resetFlag]);

  return {
    keywords,
    options,
    value,
    onChange: handleChange,
    onDelete: handleDelete,
    onSearch,
    onOpen: handleOpen,
  };
}
